import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FileUpload from "./FileUpload";

interface ProfileImageUploadProps {
  profilePicture: string;
  onFileSelect: (file: File) => void;
  isUploading?: boolean;
  isEditing?: boolean;
  className?: string;
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  profilePicture,
  onFileSelect,
  isUploading = false,
  isEditing = false,
  className = "",
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Dummy image fallback
  const dummyImage = "/images/dummy-profile.webp";
  const imageSrc = profilePicture && !imageError ? profilePicture : dummyImage;

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  return (
    <div className={`relative group ${className}`}>
      {/* Profile Image Container */}
      <div
        className="relative w-36 h-36 md:w-48 md:h-48 rounded-full overflow-hidden shadow-2xl border-4 border-white ring-4 ring-gray-100 transition-all duration-300 group-hover:scale-105 group-hover:shadow-3xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Main Image */}
        <img
          src={imageSrc}
          alt="Profile"
          className="w-full h-full object-cover transition-all duration-300"
          onError={handleImageError}
          onLoad={handleImageLoad}
        />

        {/* Loading Overlay */}
        {isUploading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          >
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </motion.div>
        )}

        {/* Hover Overlay - Only show when editing */}
        <AnimatePresence>
          {isHovered && !isUploading && isEditing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center"
            >
              <FileUpload onFileSelect={onFileSelect} accept="image/*">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white/90 backdrop-blur-sm text-gray-800 px-4 py-2 rounded-full shadow-lg hover:bg-white transition-all duration-200 flex items-center gap-2 font-medium"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Upload
                </motion.button>
              </FileUpload>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image Status Indicator */}
        {!profilePicture && (
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
          </div>
        )}
      </div>

      {/* Profile Picture Label */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="text-center mt-4"
      >
        {isEditing && (
          <>
            <p className="text-sm text-gray-600 font-medium">
              {profilePicture ? "Change Profile Picture" : "Add Profile Picture"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Hover over image to upload
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ProfileImageUpload;
